/**
 * useTour Hook
 * Manages tour state and provides tour step configuration with targets
 */
import { useEffect, useMemo } from "react"
import { useTourStore } from "stores/tourStore"
import { TOUR_STEPS } from "features/QuestionnaireEditor/components/Tour/TourSteps"
import type { TourProps } from "antd"

interface UseTourProps {
  /** Ref to the Labels button in VariantsMenuBar */
  labelsButtonRef?: React.RefObject<HTMLElement | null>
  /** Ref to the Structure Panel button */
  structurePanelButtonRef?: React.RefObject<HTMLElement | null>
  /** Ref to the Add Element button */
  addElementButtonRef?: React.RefObject<HTMLElement | null>
  /** Ref to the Element Highlighting button */
  elementHighlightButtonRef?: React.RefObject<HTMLElement | null>
  /** Ref to the editor container for context menu step */
  editorContainerRef?: React.RefObject<HTMLElement | null>
  /** Whether to auto-start the tour for first-time users */
  autoStart?: boolean
}

export const useTour = ({
  labelsButtonRef,
  structurePanelButtonRef,
  addElementButtonRef,
  elementHighlightButtonRef,
  editorContainerRef,
  autoStart = true,
}: UseTourProps = {}) => {
  const { hasSeenTour, isTourActive, startTour, completeTour } = useTourStore()

  // Auto-start tour for first-time users
  useEffect(() => {
    if (autoStart && !hasSeenTour && !isTourActive) {
      // Small delay to ensure DOM is fully rendered
      const timer = setTimeout(() => {
        startTour()
      }, 500)
      return () => clearTimeout(timer)
    }
  }, [autoStart, hasSeenTour, isTourActive, startTour])

  // Map steps to include target refs
  const tourSteps: TourProps["steps"] = useMemo(() => {
    return TOUR_STEPS.map((step, index) => {
      let target: (() => HTMLElement | null) | undefined = undefined

      // Map steps to their corresponding refs
      // Target functions are evaluated at render time, so we return functions
      // that will get the current ref value when called by the Tour component
      switch (index) {
        case 0: // Welcome
          target = undefined
          break
        case 1: // Labels Manager
          target = labelsButtonRef ? () => labelsButtonRef.current : undefined
          break
        case 2: // Structure Panel
          target = structurePanelButtonRef ? () => structurePanelButtonRef.current : undefined
          break
        case 3: // Add Element Modal
          target = addElementButtonRef ? () => addElementButtonRef.current : undefined
          break
        case 4: // Element Highlighting
          target = elementHighlightButtonRef ? () => elementHighlightButtonRef.current : undefined
          break
        case 5: // Context Menu (editor area)
          target = editorContainerRef ? () => editorContainerRef.current : undefined
          break
        case 6: // Completion
          target = undefined
          break
      }

      return {
        title: step.title,
        description: step.description,
        target: target as (() => HTMLElement) | (() => null) | undefined,
        placement: step.placement,
        nextButtonProps: step.nextButtonProps,
      }
    })
  }, [
    labelsButtonRef,
    structurePanelButtonRef,
    addElementButtonRef,
    elementHighlightButtonRef,
    editorContainerRef,
  ])

  const handleTourClose = () => {
    completeTour()
  }

  const handleTourFinish = () => {
    completeTour()
  }

  return {
    isTourActive,
    tourSteps,
    startTour,
    handleTourClose,
    handleTourFinish,
  }
}
